import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import surfAsset from "@/assets/delivery-surf.jpg.asset.json";
import islandAsset from "@/assets/delivery-island.jpg.asset.json";

export const Route = createFileRoute("/delivery")({
  head: () => ({
    meta: [
      { title: "Delivery — Double Lion" },
      { name: "description", content: "Every Double Lion binocular ships in a custom, Pelican-style waterproof hard case — built to survive the journey and become part of the kit." },
      { property: "og:title", content: "Delivery — Double Lion" },
      { property: "og:description", content: "Custom Double Lion Pelican-style waterproof case. Engineered to protect, designed to last." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://double-lion-muse.lovable.app/delivery" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://double-lion-muse.lovable.app/delivery" },
    ],
  }),
  component: DeliveryPage,
});

function DeliveryPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-32">
        {/* Hero */}
        <section className="mx-auto max-w-5xl px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Delivery
          </p>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Arrives in a case<br />built to go the distance.
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-muted-foreground leading-relaxed">
            Every pair of binoculars ships inside a custom Double Lion Pelican-style waterproof hard case — designed to be taken on a sailing voyage or the overhead bin. 
          </p>
        </section>

        {/* The Case */}
        <section className="mx-auto max-w-6xl px-6 mt-32 grid md:grid-cols-2 gap-16 items-start">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              01 — The Case
            </p>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">
              &nbsp;Waterproof hard shell
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              We worked with a Pelican-style manufacturer to develop a case sized exactly to
              DL1's — no wasted volume, no rattle. The shell is injection-molded from
              high-impact copolymer resin, with reinforced hinges and a pair of
              double-throw latches that audibly snap shut.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              An automatic pressure-equalization valve lets the case travel from sea level to
              35,000 feet without straining the seal. A continuous rubber O-ring keeps the
              interior dry to IP67 — submersible to one meter for thirty minutes.
            </p>
          </div>
          <div className="space-y-4">
            <dl className="border-t border-border/60">
              {[
                ["Construction", "Injection-molded copolymer resin"],
                ["Seal", "Continuous rubber O-ring, IP67"],
                ["Latches", "Dual double-throw, stainless reinforced"],
                ["Pressure valve", "Automatic, altitude-equalizing"],
                ["Interior", "Custom-cut closed-cell foam, charcoal"],
                ["Exterior", "Matte black with debossed double-lion mark"],
                ["Temperature range", "−40°C to 90°C"],
                ["Dimensions", "210 × 165 × 75 mm"],
                ["Weight (empty)", "640 g"],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-2 gap-6 py-4 border-b border-border/60">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{k}</dt>
                  <dd className="text-sm">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>


        {/* Whats inside */}
        <section className="mx-auto max-w-5xl px-6 mt-32">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            03 — What's Inside
          </p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">
            The kit
          </h2>
          <ul className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-6">
            {[
              ["Waterproof hard case", "Custom Double Lion screen printed hard case shell."],
              ["Microfiber pouch", "Waxed canvas exterior, brushed cotton lining."],
              ["Lens cloth", "Microfiber 80% polyester, 20%\u00a0polyamide"],
              ["Neck cord", "Durable Nylon\u00a0"],
              ["Certificate of authenticity", "Letterpress, numbered, signed."],
              ["Care guide", "Printed and in box"],
            ].map(([k, v]) => (
              <li key={k} className="border-t border-border/60 pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em]">{k}</p>
                <p className="mt-2 text-sm text-muted-foreground">{v}</p>
              </li>
            ))}
          </ul>
        </section>


        {/* Image banner */}
        <section className="mx-auto max-w-6xl px-6 mt-32 grid grid-cols-2 gap-2 md:gap-4">
          <img
            src={surfAsset.url}
            alt="Surfer riding a wave in remote tropical waters"
            loading="lazy"
            className="w-full h-48 md:h-64 object-cover"
          />
          <img
            src={islandAsset.url}
            alt="Remote palm-covered island at dusk"
            loading="lazy"
            className="w-full h-48 md:h-64 object-cover"
          />
        </section>

      </main>
    </div>
  );
}
